import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Pencil, X } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';
import { TriInput } from './MissionsManager';

const EMPTY = {
  name: '',
  city_fr: '', city_en: '', city_zh: '',
  founded: '',
  employees_fr: '', employees_en: '', employees_zh: '',
  specialty_fr: '', specialty_en: '', specialty_zh: '',
  products_fr: [], products_en: [], products_zh: [],
  certifications: [],
};

export default function CompaniesManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get('/admin/companies-of-month'); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const startEdit = (c) => {
    setEditing(c.id);
    const products = c.products || [];
    setForm({
      name: c.name || '',
      city_fr: c.city?.fr || '', city_en: c.city?.en || '', city_zh: c.city?.zh || '',
      founded: c.founded || '',
      employees_fr: c.employees?.fr || '', employees_en: c.employees?.en || '', employees_zh: c.employees?.zh || '',
      specialty_fr: c.specialty?.fr || '', specialty_en: c.specialty?.en || '', specialty_zh: c.specialty?.zh || '',
      products_fr: products.map((p) => p.fr || ''),
      products_en: products.map((p) => p.en || ''),
      products_zh: products.map((p) => p.zh || ''),
      certifications: c.certifications || [],
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === 'new') await api.post('/admin/companies-of-month', form);
      else await api.put(`/admin/companies-of-month/${editing}`, form);
      toast({ title: 'Entreprise enregistrée' });
      cancel(); load();
    } catch (err) {
      console.error('CompaniesManager save error', err?.response || err);
      const detail = err?.response?.data?.detail;
      const detailStr = Array.isArray(detail) ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(' • ') : (detail || err?.message || 'Impossible');
      toast({ title: `Erreur ${err?.response?.status || ''}`.trim(), description: detailStr, duration: 15000 });
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    await api.delete(`/admin/companies-of-month/${id}`);
    load();
  };

  const updateProduct = (i, lang, val) => {
    const key = `products_${lang}`;
    const arr = [...(form[key] || [])];
    arr[i] = val;
    setForm({ ...form, [key]: arr });
  };
  const addProduct = () => setForm({ ...form, products_fr: [...form.products_fr, ''], products_en: [...form.products_en, ''], products_zh: [...form.products_zh, ''] });
  const removeProduct = (i) => setForm({ ...form,
    products_fr: form.products_fr.filter((_, k) => k !== i),
    products_en: form.products_en.filter((_, k) => k !== i),
    products_zh: form.products_zh.filter((_, k) => k !== i),
  });

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-brand-navy">Entreprises ({items.length})</h2>
          <button onClick={() => { setEditing('new'); setForm(EMPTY); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand-navy text-white text-xs font-semibold">
            <Plus className="w-3.5 h-3.5" /> Nouvelle
          </button>
        </div>
        {items.length === 0 ? <p className="text-sm text-gray-400 italic">Aucune.</p> : (
          <ul className="space-y-2 max-h-[600px] overflow-y-auto">
            {items.map((c) => (
              <li key={c.id} className="p-3 rounded border border-gray-100 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-brand-navy text-sm truncate">{c.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{c.city?.fr} • {c.employees?.fr}</div>
                  <div className="text-[10px] text-gray-400 mt-1 truncate">{c.specialty?.fr}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEdit(c)} className="p-1.5 rounded hover:bg-gray-100"><Pencil className="w-4 h-4 text-brand-navy" /></button>
                  <button onClick={() => del(c.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-brand-navy">{editing === 'new' ? 'Nouvelle entreprise' : editing ? 'Modifier' : 'Sélectionner'}</h2>
          {editing && <button onClick={cancel} className="text-xs text-gray-500 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Annuler</button>}
        </div>
        {!editing ? <p className="text-sm text-gray-400 italic">Sélectionnez ou créez une entreprise.</p> : (
          <form onSubmit={save} className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-brand-navy uppercase mb-1">Nom (même dans les 3 langues)</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm" />
            </div>
            <TriInput label="Ville" fr={form.city_fr} en={form.city_en} zh={form.city_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['city_fr', 'city_en', 'city_zh']} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-brand-navy uppercase mb-1">Année fondation</label>
                <input value={form.founded} onChange={(e) => setForm({ ...form, founded: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-brand-navy uppercase mb-1">Certifications (séparées par ,)</label>
                <input value={form.certifications.join(', ')} onChange={(e) => setForm({ ...form, certifications: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm" placeholder="ISO 9001, CE, RoHS" />
              </div>
            </div>
            <TriInput label="Effectif" fr={form.employees_fr} en={form.employees_en} zh={form.employees_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['employees_fr', 'employees_en', 'employees_zh']} />
            <TriInput label="Spécialité" fr={form.specialty_fr} en={form.specialty_en} zh={form.specialty_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['specialty_fr', 'specialty_en', 'specialty_zh']} textarea />

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-brand-navy uppercase">Produits</label>
                <button type="button" onClick={addProduct} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-brand-yellow text-brand-navy text-xs font-semibold">
                  <Plus className="w-3 h-3" /> Ajouter
                </button>
              </div>
              {form.products_fr.length === 0 && <p className="text-xs text-gray-400 italic">Aucun produit.</p>}
              {form.products_fr.map((_, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 mb-2 items-center relative">
                  <input placeholder="FR" value={form.products_fr[i] || ''} onChange={(e) => updateProduct(i, 'fr', e.target.value)}
                    className="px-2 py-1.5 border border-gray-200 rounded text-xs" />
                  <input placeholder="EN" value={form.products_en[i] || ''} onChange={(e) => updateProduct(i, 'en', e.target.value)}
                    className="px-2 py-1.5 border border-gray-200 rounded text-xs" />
                  <div className="flex gap-1">
                    <input placeholder="中文" value={form.products_zh[i] || ''} onChange={(e) => updateProduct(i, 'zh', e.target.value)}
                      className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs" />
                    <button type="button" onClick={() => removeProduct(i)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
                  </div>
                </div>
              ))}
            </div>

            <button disabled={busy} className="btn-yellow w-full justify-center disabled:opacity-60"><Save className="w-4 h-4" /> Enregistrer</button>
          </form>
        )}
      </div>
    </div>
  );
}
